'use client';

import { ArrowSeparateVertical } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import {
  Button as RAC_Button,
  ComboBox as RAC_ComboBox,
  Input as RAC_Input,
  Label as RAC_Label,
  ListBox as RAC_ListBox,
  ListBoxItem as RAC_ListBoxItem,
  Popover as RAC_Popover
} from 'react-aria-components';
import labelStyles from '~/components/Label/src/Label.module.css';
import styles from './Combobox.module.css';
import type { ComboboxItemIconProps, ComboboxItemProps, ComboboxProps } from './Combobox.types';

const Combobox = forwardRef<HTMLDivElement, ComboboxProps<object>>(
  (
    {
      name,
      children,
      label,
      multiple,
      placeholder,
      border = 'default',
      hasButton = false,
      shouldFlip = true,
      placement,
      popoverRef,
      ...props
    },
    ref
  ) => {
    const t = useTranslations('aria');
    return (
      <RAC_ComboBox
        className={clsx(styles.combobox, {
          [styles.borderBottom]: border === 'bottom',
          [styles.borderDefault]: border === 'default',
          [styles.borderNone]: border === 'none',
          [styles.noLabel]: !label
        })}
        ref={ref}
        {...props}
      >
        {label && (
          <RAC_Label className={clsx(labelStyles.label, labelStyles.small)} id={name}>
            {label}
          </RAC_Label>
        )}
        <div className={styles.inputContainer}>
          <RAC_Input placeholder={placeholder} className={styles.input} />
          {hasButton && (
            <RAC_Button>
              <ArrowSeparateVertical size="large" ariaLabel={t('options')} />
            </RAC_Button>
          )}
        </div>
        {props.items && Array.from(props.items).length > 0 && (
          <RAC_Popover
            ref={popoverRef}
            offset={8}
            crossOffset={-8}
            className={styles.popover}
            shouldFlip={shouldFlip}
            placement={placement}
          >
            <RAC_ListBox selectionMode={multiple ? 'multiple' : 'single'}>
              {(listBoxProps) => (typeof children === 'function' ? children(listBoxProps) : children)}
            </RAC_ListBox>
          </RAC_Popover>
        )}
      </RAC_ComboBox>
    );
  }
);

Combobox.displayName = 'Combobox';

const ComboboxItem = forwardRef<HTMLDivElement, ComboboxItemProps>(({ children, ...props }: ComboboxItemProps, ref) => {
  return (
    <RAC_ListBoxItem ref={ref} className={styles.comboboxItem} {...props}>
      {children}
    </RAC_ListBoxItem>
  );
});

ComboboxItem.displayName = 'ComboboxItem';

const ComboboxItemIcon = forwardRef<HTMLSpanElement, ComboboxItemIconProps>(
  ({ children }: ComboboxItemIconProps, ref) => {
    return (
      <span className={styles.comboboxItemIcon} ref={ref}>
        {children}
      </span>
    );
  }
);

ComboboxItemIcon.displayName = 'ComboboxItemIcon';

export { Combobox, ComboboxItem, ComboboxItemIcon };
